import React from "react";

/** Left offset to clear fixed sidebar (see Sidebar.jsx ~250px + gutter). */
export const VM_SIDEBAR_GUTTER_PX = 278;

/**
 * Main content shell beside the sidebar: constrained width + minWidth 0 so wide tables
 * scroll inside the column instead of overlapping the menu or forcing page overflow.
 * @param {boolean} isMobile
 * @param {{ marginRightPx?: number }} [options]
 */
export function vmPageShell(isMobile, options = {}) {
  const marginRightPx = options.marginRightPx ?? 0;
  if (isMobile) {
    return {
      boxSizing: "border-box",
      marginLeft: 0,
      marginRight: 0,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflowX: "hidden",
      position: "relative",
      zIndex: 1,
    };
  }
  const L = VM_SIDEBAR_GUTTER_PX;
  const R = marginRightPx;
  const totalGutter = L + R;
  return {
    boxSizing: "border-box",
    marginLeft: L,
    marginRight: R,
    width: `calc(100% - ${totalGutter}px)`,
    maxWidth: `calc(100% - ${totalGutter}px)`,
    minWidth: 0,
    overflowX: "hidden",
    position: "relative",
    zIndex: 1,
  };
}

/** Wrap record tables: horizontal scroll stays within the content column. */
export const vmTableScrollWrap = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  boxSizing: "border-box",
  border: "1px solid #e2e2e2",
  borderRadius: "8px",
  backgroundColor: "#fff",
};

/** Base `<table>` for Vehicle Management record grids (merge into local `S.table`). */
export const vmRecordTable = {
  width: "100%",
  borderCollapse: "collapse",
  borderSpacing: 0,
  tableLayout: "auto",
  fontSize: "13px",
};

/** Shared `<th>` for record tables: sticky header while scrolling horizontally. */
export const vmRecordTh = {
  backgroundColor: "#FFC107",
  color: "#000",
  padding: "11px 12px",
  fontSize: "12px",
  fontWeight: "700",
  letterSpacing: "0.02em",
  textAlign: "left",
  borderBottom: "2px solid #e0a800",
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

/** Shared `<td>` for record tables. */
export const vmRecordTd = {
  padding: "10px 12px",
  fontSize: "13px",
  color: "#111",
  borderBottom: "1px solid #e8e8e8",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

/** Zebra striping for tbody rows (index is 0-based). */
export function vmRecordRowBg(index) {
  return index % 2 === 0 ? "#ffffff" : "#f9f9f9";
}

/** Pixel width per column on mobile (`table-layout: fixed`) so thead/tbody columns stay aligned */
export const VM_MOBILE_COL_PX = 88;

/**
 * Mobile: fixed table width = columnCount × VM_MOBILE_COL_PX so every column shares one grid
 * (avoids header/body drift from `table-layout: auto` + nowrap). Desktop unchanged.
 * @param {boolean} isMobile
 * @param {number} [columnCount] column count including checkbox col if present
 */
export function getVmRecordTableStyle(isMobile, columnCount) {
  const base = { ...vmRecordTable };
  if (!isMobile) return base;
  const n =
    typeof columnCount === "number" && columnCount > 0 ? Math.round(columnCount) : 10;
  const w = n * VM_MOBILE_COL_PX;
  return {
    ...base,
    tableLayout: "fixed",
    width: `${w}px`,
    minWidth: `${w}px`,
    fontSize: "12px",
    borderCollapse: "collapse",
  };
}

/** Equal-width columns on mobile (use inside `<table>` before `<thead>`). */
export function vmRecordColGroupReact(columnCount) {
  if (!columnCount || columnCount < 1) return null;
  const px = VM_MOBILE_COL_PX;
  return React.createElement(
    "colgroup",
    null,
    Array.from({ length: columnCount }, (_, i) =>
      React.createElement("col", {
        key: i,
        style: { width: `${px}px`, minWidth: `${px}px` },
      })
    )
  );
}

export function getVmRecordThStyle(isMobile) {
  return {
    ...vmRecordTh,
    ...(isMobile
      ? {
          padding: "8px 8px",
          fontSize: "11px",
          lineHeight: 1.25,
          boxSizing: "border-box",
          letterSpacing: "normal",
          textAlign: "left",
          whiteSpace: "normal",
          wordBreak: "break-word",
          verticalAlign: "top",
          minWidth: 0,
          position: "static",
          top: "auto",
          zIndex: "auto",
        }
      : {}),
  };
}

export function getVmRecordTdStyle(isMobile) {
  return {
    ...vmRecordTd,
    ...(isMobile
      ? {
          padding: "8px 8px",
          fontSize: "11px",
          lineHeight: 1.25,
          boxSizing: "border-box",
          textAlign: "left",
          whiteSpace: "normal",
          wordBreak: "break-word",
          verticalAlign: "top",
          minWidth: 0,
        }
      : {}),
  };
}
